
import React, { useEffect, useMemo } from "react";
import RoleSection from "./permissions/RoleSection";
import ErrorState from "./permissions/ErrorState";
import { Permission, Role, Resource, PermissionType } from "@/hooks/rollenbeheer/types";

interface RolePermissionsTableProps {
  permissions: Permission[];
  onUpdatePermission: (role: Role, resource: Resource, permissionType: PermissionType, value: boolean) => void;
}

const RolePermissionsTable = ({ permissions, onUpdatePermission }: RolePermissionsTableProps) => {
  // Debug log for component rendering with timestamp
  useEffect(() => {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] [RolePermissionsTable] Component (re)rendered`);
  });
  
  // Debug log for permissions prop
  useEffect(() => {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] [RolePermissionsTable] Received permissions prop with length:`, permissions?.length || 0);
    
    if (!Array.isArray(permissions) || permissions.length === 0) {
      console.warn(`[${timestamp}] [RolePermissionsTable] Invalid permissions provided`);
    } else {
      // Log sample permission
      console.log(`[${timestamp}] [RolePermissionsTable] Sample permission:`, permissions[0]);
    }
  }, [permissions]);

  // Memoize grouped permissions to prevent recalculation on each render
  const permissionsByRole = useMemo(() => {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] [RolePermissionsTable] Grouping permissions by role`);
    
    if (!permissions || !Array.isArray(permissions) || permissions.length === 0) {
      console.warn(`[${timestamp}] [RolePermissionsTable] Invalid permissions for grouping:`, permissions);
      return {};
    }
    
    try {
      // Use a try/catch to ensure any errors in processing don't break the component
      const result = permissions.reduce((acc, permission) => {
        if (!permission || !permission.role) {
          console.warn(`[${timestamp}] [RolePermissionsTable] Invalid permission item:`, permission);
          return acc;
        }
        
        if (!acc[permission.role]) {
          acc[permission.role] = [];
        }
        acc[permission.role].push(permission);
        return acc;
      }, {} as Record<Role, Permission[]>);
      
      console.log(`[${timestamp}] [RolePermissionsTable] Grouped permissions by role:`, Object.keys(result));
      return result;
    } catch (error) {
      console.error(`[${timestamp}] [RolePermissionsTable] Error grouping permissions:`, error);
      return {};
    }
  }, [permissions]);

  const roles = useMemo(() => {
    return Object.keys(permissionsByRole) as Role[];
  }, [permissionsByRole]);

  // Handle switch toggle with additional error handling and debug logging
  const handleSwitchToggle = (role: Role, resource: Resource, permissionType: PermissionType, currentValue: boolean) => {
    const timestamp = new Date().toISOString();
    try {
      console.log(`[${timestamp}] [RolePermissionsTable] Switch toggled: ${role} - ${resource} - ${permissionType}: ${currentValue} -> ${!currentValue}`);
      onUpdatePermission(role, resource, permissionType, !currentValue);
    } catch (error) {
      console.error(`[${timestamp}] [RolePermissionsTable] Error toggling permission:`, error);
    }
  };

  // If permissions is invalid, show an error
  if (!permissions || !Array.isArray(permissions) || permissions.length === 0) {
    console.error(`[${new Date().toISOString()}] [RolePermissionsTable] No permissions data found`);
    return <ErrorState type="invalid" />;
  }

  // If we have no roles, show an empty state
  if (roles.length === 0) {
    console.warn(`[${new Date().toISOString()}] [RolePermissionsTable] No roles found in permissions data`);
    return <ErrorState type="empty" />;
  }

  return (
    <div>
      {roles.map((role, index) => (
        <div key={role} className={index > 0 ? "mt-8" : ""}>
          <RoleSection 
            role={role}
            permissions={permissionsByRole[role]}
            onUpdatePermission={handleSwitchToggle}
          />
        </div>
      ))}
    </div>
  );
};

export default RolePermissionsTable;
